import React, { useState } from 'react';
import { Box, Typography, Stack, Button, Alert, CircularProgress } from '@mui/material';
import { useLanguage } from '../context/LanguageContext';
import { BORDER, BRAND, FOCUS_RING, INK_SOFT, SURFACE } from '../pages/Practice/map/mapTokens';

const languages = [
  { code: 'fr', label: 'French', flag: '/flags/fr.svg' },
  { code: 'de', label: 'German', flag: '/flags/de.svg' },
];

/**
 * The segmented rendering of the same choice, for places where this control sits
 * next to calls to action.
 *
 * Two standalone pills are indistinguishable from the buttons beside them, so a
 * visitor reads "French / German / Start practising / See the exam structure" as
 * one set of four options. An inset joined track is the app's established
 * signifier for "pick one of these" (see SegmentedSwitch in the practice map) and
 * separates the setting from the actions without needing a caption to explain it.
 */
const SegmentedLanguage = ({ language, pendingLanguage, disabled, onSelect }) => (
  <Stack
    direction="row"
    role="radiogroup"
    aria-label="Language you are learning"
    sx={{
      width: '100%',
      maxWidth: 320,
      mx: { xs: 'auto', md: 0 },
      p: 0.5,
      gap: 0.5,
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 10px 24px -14px rgba(16,24,40,.14)',
    }}
  >
    {languages.map(({ code, label, flag }) => {
      const isActive = language === code;
      const isSaving = pendingLanguage === code;

      return (
        <Box
          key={code}
          component="button"
          type="button"
          role="radio"
          aria-checked={isActive}
          disabled={disabled}
          onClick={() => onSelect(code)}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            borderRadius: '10px',
            py: 1,
            px: 1.25,
            bgcolor: isActive ? BRAND : 'transparent',
            color: isActive ? SURFACE : INK_SOFT,
            fontWeight: 800,
            fontSize: '0.9375rem',
            transition: 'background-color .2s ease, color .2s ease',
            '&:hover': { bgcolor: isActive ? undefined : `${BRAND}12` },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            ...FOCUS_RING,
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <img
              src={flag}
              alt=""
              aria-hidden
              style={{ width: 22, height: 'auto', borderRadius: 2, opacity: isSaving ? 0.35 : 1 }}
            />
            {isSaving && <CircularProgress size={16} color="inherit" sx={{ position: 'absolute', left: 3 }} />}
          </Box>
          {label}
        </Box>
      );
    })}
  </Stack>
);

const LanguageSelectionSection = ({ compact = false, variant = 'buttons' }) => {
  const { language, changeLanguage, lockedMessage, languageLocked, languageChangePending, pendingLanguage } =
    useLanguage();
  const [errorMessage, setErrorMessage] = useState('');

  const handleSelect = async (code) => {
    const result = await changeLanguage(code);
    if (!result.success && result.message) {
      setErrorMessage(result.message);
      return;
    }
    setErrorMessage('');
  };

  return (
    <Box
      component="section"
      sx={{
        py: compact ? 0 : { xs: 6, md: 8 },
        px: compact ? 0 : { xs: 2, sm: 4, md: 8 },
        textAlign: compact ? 'left' : 'center',
      }}
    >
      {!compact ? (
        <>
          <Typography variant="h3" component="h2" fontWeight={900} color="primary.main" sx={{ mb: 2 }}>
            Choose your practice language
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: { xs: 4, md: 6 } }}>
            Switch between French and German to tailor every topic, resource, and lesson.
          </Typography>
        </>
      ) : null}

      {!compact && languageLocked && (
        <Alert severity="info" sx={{ maxWidth: 480, mx: 'auto', mb: { xs: 3, md: 4 } }}>
          {lockedMessage}
        </Alert>
      )}

      {errorMessage && !languageLocked && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          sx={{ maxWidth: 480, mx: compact ? 0 : 'auto', mb: compact ? 1.5 : { xs: 3, md: 4 } }}
        >
          {errorMessage}
        </Alert>
      )}

      {variant === 'segmented' ? (
        <SegmentedLanguage
          language={language}
          pendingLanguage={pendingLanguage}
          disabled={languageChangePending}
          onSelect={handleSelect}
        />
      ) : (
        <Stack
          direction={compact ? 'row' : { xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent={compact ? 'flex-end' : 'center'}
          alignItems="center"
        >
          {languages.map(({ code, label, flag }) => {
            const isActive = language === code;
            const isSaving = pendingLanguage === code;
            return (
              <Button
                key={code}
                variant={isActive ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => handleSelect(code)}
                disabled={isActive || languageChangePending}
                aria-pressed={isActive}
                sx={{
                  minWidth: compact ? { xs: 0, sm: 145 } : 220,
                  flex: compact ? 1 : 'initial',
                  py: compact ? 1.1 : { xs: 1.5, sm: 2 },
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  // The selected language is disabled so it cannot be re-picked, but
                  // MUI's disabled styling greys it out — which reads as
                  // "unavailable" rather than "this is your choice". Keep the
                  // selected look for the active one; only a genuine pending save
                  // should look inert.
                  ...(isActive && !languageChangePending
                    ? {
                        '&.Mui-disabled': {
                          opacity: 1,
                          color: 'common.white',
                          backgroundColor: 'secondary.main',
                        },
                      }
                    : null),
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 32,
                    height: 22,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={flag}
                    alt={`${label} flag`}
                    style={{
                      width: '32px',
                      height: 'auto',
                      borderRadius: '2px',
                      objectFit: 'cover',
                      opacity: isSaving ? 0.35 : 1,
                    }}
                  />
                  {isSaving && <CircularProgress size={22} color="inherit" sx={{ position: 'absolute' }} />}
                </Box>
                {label}
              </Button>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default LanguageSelectionSection;
